/* eslint-disable @typescript-eslint/naming-convention */
'use client';

import React from 'react';

import { cx } from '@pt-group-fe/styled-system/css';
import { ImageVariantProps, image as imagex } from '@pt-group-fe/styled-system/recipes';
import type { ImageProps as NextImageProps } from 'next/image';
import type { ImageLoader } from 'next/image';
import Image from 'next/image';
import { keys } from 'remeda';

import { BREAK_POINTS } from '@lib/config';
import { image } from '@lib/services/image';
import { BreakPointKey } from '@lib/types';

interface ImgWidths {
  _?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
  '2xl'?: number;
  '3xl'?: number;
  '4xl'?: number;
  '5xl'?: number;
  '6xl'?: number;
}

interface ImgProps extends Omit<NextImageProps, 'alt' | 'sizes' | 'src'>, ImageVariantProps {
  alt?: string;
  fallbackAlt?: string;
  data?: any; // Cms_SingleMedia;
  transform?: any;
  sizes?: any; // ImgSizes;
  widths?: ImgWidths;
  resize?: {
    fit?: 'contain' | 'cover';
    withoutEnlargement?: boolean;
  };
}

const Img = React.forwardRef<HTMLImageElement, ImgProps>((props, ref) => {
  const {
    resize,
    transform,
    loader,
    width,
    height,
    alt,
    data,
    fallbackAlt,
    widths,
    className,
    sizes,
    fill,
    variant,
    ...restProps
  } = props;

  const imageLoader: ImageLoader = loaderProps => {
    const tr = {
      ...transform,
      resize: {
        w: loaderProps.width,
        fill: resize?.fit || 'cover'
      }
    };

    if (data.data?.url.search('cms-strapi-psw-docs') !== -1) {
      return `${image.getImgUrlFromKey(loaderProps.src, tr)}&docs=true`;
    }

    return image.getImgUrlFromKey(loaderProps.src, tr);
  };

  const getSizes = () => {
    if (width && height && !sizes && !widths) return undefined;

    const calcSizes: string[] = [];

    (keys(props.sizes || {}) as BreakPointKey[]).forEach(key => {
      const val = props.sizes?.[key];

      if (val) {
        calcSizes.push(`(min-width: ${BREAK_POINTS[key]}px) ${val}vw`);
      }
    });

    if (!widths?._) {
      calcSizes.push('100vw');
    }

    return calcSizes.join(', ');
  };

  if (!data || !data.data) return null;

  return (
    <Image
      ref={ref}
      className={cx('img', imagex({ variant: fill ? variant || 'cover' : undefined }), className)}
      sizes={getSizes()}
      width={width}
      height={height}
      loader={loader || imageLoader}
      src={image.getKeyFormS3Url(data.data?.url)}
      alt={alt || data.data.alternativeText || fallbackAlt || 'PHOENIX-SYSTEMS'}
      fill={fill}
      {...restProps}
    />
  );
});
Img.displayName = 'Img';

export { Img };
export type { ImgProps, ImgWidths };
